import { Fragment, useCallback } from 'react';
import { useDrop } from 'react-dnd';

import styled from '@emotion/styled';

import { type DropSide, FAVORITE_DRAG_TYPE, type FavoriteDragItem } from './constants';
import { FavoriteBarDropGapDivider } from './FavoriteDropGapDivider';
import FavoriteItem from './FavoriteItem';

import type { ReactNode, Ref, RefObject } from 'react';
import type { FavoriteService } from '@kc-console/context';

interface FavoriteListProps {
  dropIndicatorGap: number | null;
  favoriteItems: FavoriteService[];
  itemRefs: RefObject<Map<string, HTMLButtonElement>>;
  onDragEnd: (didDrop: boolean) => void;
  onDragHover: (from: number, to: number, side: DropSide) => void;
  visibleItems: FavoriteService[];
  children?: ReactNode;
  ref?: Ref<HTMLUListElement>;
}

const FavoriteList = ({
  children,
  dropIndicatorGap,
  favoriteItems,
  itemRefs,
  onDragEnd,
  onDragHover,
  visibleItems,
  ref,
}: FavoriteListProps) => {
  const registerItemRef = useCallback(
    (serviceName: string, element: HTMLButtonElement | null) => {
      if (element) {
        itemRefs.current.set(serviceName, element);
      } else {
        itemRefs.current.delete(serviceName);
      }
    },
    [itemRefs],
  );

  // 아이템 사이 gap/indicator(Divider) 영역에 드롭해도 커밋되도록
  // 목록 컨테이너 전체를 드롭 타깃으로 등록한다. 삽입 위치는 직전 hover로 갱신된 dragState를 사용
  const [, drop] = useDrop<FavoriteDragItem>({
    accept: FAVORITE_DRAG_TYPE,
    // + 버튼/후행 빈 영역(마지막 노출 아이템 오른쪽)은 "노출 목록 맨 끝으로 이동"으로 해석해
    // 끝 위치 인디케이터를 표시한다. 아이템 사이 틈에서 끝으로 튀지 않도록 x 좌표로 제한한다.
    hover: (dragItem, monitor) => {
      // 아이템이 아닌 ul 직접 영역일 때만 평가
      if (!monitor.isOver({ shallow: true })) return;
      const lastVisibleItem = visibleItems[visibleItems.length - 1];
      if (!lastVisibleItem) return;
      const offset = monitor.getClientOffset();
      const lastRect = itemRefs.current.get(lastVisibleItem.name)?.getBoundingClientRect();
      if (!offset || !lastRect || offset.x <= lastRect.right) return;
      onDragHover(dragItem.index, visibleItems.length - 1, 'after');
    },
    drop: () => ({ dropped: true }),
  });

  const composedListRef = useCallback(
    (element: HTMLUListElement | null) => {
      drop(element);
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [drop, ref],
  );

  return (
    <StyledFavoriteList ref={composedListRef}>
      {favoriteItems.map((service, idx) => {
        const isVisible = visibleItems.some(v => v.name === service.name);
        return (
          <Fragment key={service.name}>
            <FavoriteBarDropGapDivider hidden={dropIndicatorGap !== idx} />
            <FavoriteItemWrapper isVisible={isVisible}>
              <FavoriteItem
                service={service}
                index={idx}
                onDragHover={onDragHover}
                onDragEnd={onDragEnd}
                ref={element => registerItemRef(service.name, element)}
                isVisible={isVisible}
              />
            </FavoriteItemWrapper>
          </Fragment>
        );
      })}
      <FavoriteBarDropGapDivider hidden={dropIndicatorGap !== favoriteItems.length} />
      {children}
    </StyledFavoriteList>
  );
};

export default FavoriteList;

const StyledFavoriteList = styled('ul')`
  position: relative;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  flex-wrap: nowrap;

  > * {
    flex-shrink: 0;
    flex-grow: 0;
  }
`;

const FavoriteItemWrapper = styled('li')<{ isVisible: boolean }>`
  ${({ isVisible }) =>
    !isVisible &&
    `
      position: absolute;
      visibility: hidden;
      pointer-events: none;
    `}
`;
